import React, { useState, useEffect, useRef } from 'react';
import { doc, setDoc, updateDoc, addDoc, serverTimestamp, collection, query, orderBy, onSnapshot, getDocs, where } from '../../lib/auditedFirestore'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import Spinner from '../../components/Spinner';
import AdvancedWordProcessor from '../../components/editors/AdvancedWordProcessor';
import CustomSpreadsheet from '../../components/editors/CustomSpreadsheet';

const HomeworkRoom = ({ homework, user, onBack, reviewMode = false, reviewTargetStudent = null, reviewSubmission = null }) => {
    // --- UI State ---
    const [activeTab, setActiveTab] = useState('word');
    const [leftPanelWidth, setLeftPanelWidth] = useState(40);
    const [showMaterial, setShowMaterial] = useState(true);
    const [showChat, setShowChat] = useState(false); // Toggle Chat Sidebar
    
    // --- Content State ---
    const [wordContent, setWordContent] = useState('');
    const [excelData, setExcelData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [submissionId, setSubmissionId] = useState(null);
    
    // --- NEW: Status State ---
    const [submissionStatus, setSubmissionStatus] = useState(null); // 'submitted' | 'reviewed' | null

    // --- Chat State ---
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSendingMsg, setIsSendingMsg] = useState(false);
    const chatScrollRef = useRef(null);

    // --- Audio Recording State ---
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    // --- 1. Fetch Submission Data ---
    useEffect(() => {
        const fetchWork = async () => {
            try {
                let subId = null;

                // 1. Review Mode (Admin viewing Student work)
                if (reviewMode && reviewSubmission) {
                    setWordContent(reviewSubmission.wordContent || '');
                    setExcelData(reviewSubmission.excelData || null);
                    setSubmissionStatus(reviewSubmission.status || 'submitted');
                    subId = reviewSubmission.id;
                }
                // 2. Student Mode (Viewing own work)
                else {
                    const q = query(
                        collection(db, "homeworkSubmissions"),
                        where("homeworkId", "==", homework.id),
                        where("userId", "==", user.uid)
                    );
                    const snapshot = await getDocs(q);

                    if (!snapshot.empty) {
                        const data = snapshot.docs[0].data();
                        subId = snapshot.docs[0].id;
                        setWordContent(data.wordContent || '');
                        setExcelData(data.excelData || null);
                        setSubmissionStatus(data.status || 'submitted');
                    }
                }

                setSubmissionId(subId);
                setIsLoading(false);

                // --- Mark messages as read when opening ---
                if (subId) {
                    const fieldToUpdate = reviewMode ? 'unreadByAdmin' : 'unreadByStudent';
                    await updateDoc(doc(db, "homeworkSubmissions", subId), {
                        [fieldToUpdate]: false
                    });
                }

            } catch (error) {
                console.error("Error fetching work:", error);
                setIsLoading(false);
            }
        };
        fetchWork();
    }, [homework.id, user.uid, reviewMode, reviewSubmission]);

    // --- 2. Listen for Chat Messages ---
    useEffect(() => {
        if (!submissionId) return;

        const q = query(
            collection(db, "homeworkSubmissions", submissionId, "messages"),
            orderBy("createdAt", "asc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMessages(msgs);
            // Auto-scroll to bottom
            if (chatScrollRef.current) {
                setTimeout(() => {
                    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
                }, 100);
            }
        });

        return () => unsubscribe();
    }, [submissionId]);

    // --- 3. Save Work Handler ---
    const handleSave = async () => {
        setIsSaving(true);
        try {
            const newStatus = reviewMode ? 'reviewed' : 'submitted';
            
            const data = {
                homeworkId: homework.id,
                userId: reviewMode ? reviewTargetStudent.id : user.uid, 
                studentName: reviewMode ? reviewTargetStudent.name : (user.name || user.displayName),
                wordContent: wordContent,
                excelData: excelData,
                submittedAt: serverTimestamp(),
                status: newStatus
            };

            if (submissionId) {
                await setDoc(doc(db, "homeworkSubmissions", submissionId), data, { merge: true });
            } else {
                const ref = doc(collection(db, "homeworkSubmissions"));
                await setDoc(ref, data);
                setSubmissionId(ref.id);
            }
            
            setSubmissionStatus(newStatus); // Update UI immediately
            alert(reviewMode ? "Corrections sent successfully!" : "Homework submitted successfully!");
        } catch (error) {
            console.error("Save error:", error);
            alert("Failed to save.");
        } finally {
            setIsSaving(false);
        }
    };

    // --- 4. Audio Functions ---
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            chunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (err) {
            console.error("Microphone access denied:", err);
            alert("Could not access microphone.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
    };

    const cancelRecording = () => {
        setAudioBlob(null);
        chunksRef.current = [];
    };

    // --- 5. Send Message (Text or Audio) ---
    const handleSendMessage = async () => {
        if ((!newMessage.trim() && !audioBlob) || !submissionId) return;
        setIsSendingMsg(true);

        try {
            let audioUrl = null;

            // Upload Audio if exists
            if (audioBlob) {
                const audioRef = ref(storage, `chat_audio/${submissionId}/${Date.now()}.webm`);
                await uploadBytes(audioRef, audioBlob);
                audioUrl = await getDownloadURL(audioRef);
            }

            // 1. Add Message to Subcollection
            await addDoc(collection(db, "homeworkSubmissions", submissionId, "messages"), {
                text: newMessage,
                audioUrl: audioUrl,
                senderId: user.uid,
                senderName: user.name || user.displayName || (reviewMode ? 'Instructor' : 'Student'),
                role: reviewMode ? 'admin' : 'student',
                createdAt: serverTimestamp()
            });

            // 2. Update Parent Submission (Unread Flags)
            const updateData = {
                lastMessageAt: serverTimestamp(),
                // If I am admin, mark unread for student. If I am student, mark unread for admin.
                unreadByStudent: reviewMode ? true : false,
                unreadByAdmin: reviewMode ? false : true 
            };
            await updateDoc(doc(db, "homeworkSubmissions", submissionId), updateData);

            // 3. Send Notification to Recipient
            if (reviewMode) {
                // Admin sending to Student
                await addDoc(collection(db, "notifications"), {
                    type: 'homework_chat',
                    title: `Feedback: ${homework.title}`,
                    message: `Instructor sent a message regarding your homework.`,
                    paperId: homework.paperId || null,
                    targetUserId: reviewTargetStudent?.id || null, // Specific student
                    createdAt: serverTimestamp()
                });
            } else {
                // Student sending to Admin
                await addDoc(collection(db, "notifications"), {
                    type: 'homework_chat',
                    title: `Homework Question: ${homework.title}`,
                    message: `${user.name} sent a message regarding their submission.`,
                    paperId: homework.paperId || null,
                    targetAudience: ['admin'], // All admins
                    createdAt: serverTimestamp()
                });
            }

            setNewMessage('');
            setAudioBlob(null);
            chunksRef.current = [];
        } catch (error) {
            console.error("Send error:", error);
            alert("Failed to send message.");
        } finally {
            setIsSendingMsg(false);
        }
    };

    // --- Render Viewer ---
    const renderViewer = () => {
        if (homework.type === 'pdf') {
            return <iframe src={homework.url} className="w-full h-full border-none" title="Homework PDF" />;
        }
        return <img src={homework.url} alt="Homework" className="w-full h-auto object-contain" />;
    };

    // --- Helper: Get Status Badge ---
    const getStatusBadge = () => {
        if (!submissionId) {
            return (
                <div className="px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-500 border border-gray-200 flex items-center gap-1">
                    <span>⚪</span> Not Submitted
                </div>
            );
        }
        if (reviewMode) {
            // Admin View
            return submissionStatus === 'reviewed' ? (
                <div className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
                    <span>✅</span> Correction Sent
                </div>
            ) : (
                <div className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 border border-yellow-200 flex items-center gap-1">
                    <span>⚠️</span> Pending Review
                </div>
            );
        } else {
            // Student View
            return submissionStatus === 'reviewed' ? (
                <div className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
                    <span>✅</span> Correction Received
                </div>
            ) : (
                <div className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-1">
                    <span>📤</span> Submitted
                </div>
            );
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-100 flex flex-col z-50">
            {/* Header */}
            <header className="h-16 bg-white border-b flex items-center justify-between px-6 shadow-sm z-20">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="text-gray-600 hover:text-purple-600 font-bold flex items-center gap-2">
                        &larr; Exit
                    </button>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <div className="flex flex-col">
                        <h1 className="font-bold text-lg text-gray-800 truncate max-w-md leading-tight">
                            {reviewMode ? `Correcting: ${reviewTargetStudent?.name}` : `Homework: ${homework.title}`}
                        </h1>
                        {/* Mobile/Tablet Status Visibility: Hidden on very small screens if needed, but useful */}
                        <div className="mt-0.5">{getStatusBadge()}</div>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* Chat Toggle Button */}
                    <button 
                        onClick={() => setShowChat(!showChat)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg font-bold transition border ${showChat ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    >
                        <span>💬</span> Discussion
                    </button>

                    {isSaving && <span className="text-sm text-gray-500">Saving...</span>}
                    <button 
                        onClick={handleSave} 
                        className={`px-6 py-2 rounded font-bold text-white shadow ${reviewMode ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                        {reviewMode ? 'Save Corrections' : 'Submit Homework'}
                    </button>
                </div>
            </header>

            {/* Main Area */}
            <div className="flex-1 flex overflow-hidden relative">
                
                {/* --- Left: Question Material --- */}
                <div style={{ width: `${leftPanelWidth}%` }} className="bg-gray-200 border-r flex flex-col overflow-hidden relative transition-all">
                    <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
                        <div className="bg-white shadow-lg min-h-full rounded-lg overflow-hidden w-full h-full flex items-center justify-center">
                            {renderViewer()}
                        </div>
                    </div>
                </div>

                {/* --- Center: Work Area --- */}
                <div style={{ width: showChat ? `${100 - leftPanelWidth - 25}%` : `${100 - leftPanelWidth}%` }} className="flex flex-col bg-white transition-all duration-300">
                    <div className="flex border-b bg-gray-50">
                        <button onClick={() => setActiveTab('word')} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${activeTab === 'word' ? 'border-b-2 border-purple-600 text-purple-700 bg-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                            📝 Word
                        </button>
                        <button onClick={() => setActiveTab('excel')} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${activeTab === 'excel' ? 'border-b-2 border-purple-600 text-purple-700 bg-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                            📊 Excel
                        </button>
                    </div>

                    <div className="flex-1 overflow-hidden relative">
                        {isLoading ? <div className="absolute inset-0 flex items-center justify-center"><Spinner color="border-purple-600"/></div> : (
                            <>
                                <div className={`absolute inset-0 flex flex-col ${activeTab === 'word' ? 'block' : 'hidden'}`}>
                                    <AdvancedWordProcessor value={wordContent} onChange={setWordContent} />
                                </div>
                                <div className={`absolute inset-0 flex flex-col ${activeTab === 'excel' ? 'block' : 'hidden'}`}>
                                    <CustomSpreadsheet data={excelData} onChange={setExcelData} />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* --- Right: Chat Sidebar --- */}
                <div style={{ width: showChat ? '25%' : '0px', transform: showChat ? 'translateX(0)' : 'translateX(100%)' }} className="absolute right-0 top-0 bottom-0 bg-white border-l shadow-xl transition-all duration-300 flex flex-col z-30">
                    <div className="p-3 border-b bg-purple-50 flex justify-between items-center">
                        <h3 className="font-bold text-purple-800">Discussion & Feedback</h3>
                        <button onClick={() => setShowChat(false)} className="text-gray-500 hover:text-gray-800">&times;</button>
                    </div>

                    {/* Messages List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" ref={chatScrollRef}>
                        {messages.length === 0 && <p className="text-center text-gray-400 text-sm italic">No messages yet. Start the conversation!</p>}
                        
                        {messages.map(msg => {
                            const isMe = msg.senderId === user.uid;
                            return (
                                <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                    <div className={`max-w-[85%] rounded-lg p-3 shadow-sm ${isMe ? 'bg-purple-600 text-white rounded-br-none' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'}`}>
                                        {!isMe && <p className="text-xs font-bold mb-1 opacity-75">{msg.senderName}</p>}
                                        
                                        {/* Audio Player */}
                                        {msg.audioUrl && (
                                            <audio controls src={msg.audioUrl} className="h-8 w-full mb-1" />
                                        )}
                                        
                                        {/* Text Content */}
                                        {msg.text && <p className="text-sm whitespace-pre-wrap">{msg.text}</p>}
                                        
                                        <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-purple-200' : 'text-gray-400'}`}>
                                            {msg.createdAt?.seconds ? new Date(msg.createdAt.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '...'}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Input Area */}
                    <div className="p-3 border-t bg-white">
                        {audioBlob ? (
                            <div className="flex items-center justify-between bg-gray-100 p-2 rounded mb-2">
                                <span className="text-xs font-bold text-gray-700">🎤 Audio Recorded</span>
                                <button onClick={cancelRecording} className="text-red-500 text-xs font-bold hover:underline">Delete</button>
                            </div>
                        ) : null}

                        <div className="flex gap-2 items-end">
                            <button 
                                onClick={isRecording ? stopRecording : startRecording}
                                className={`p-2 rounded-full flex-shrink-0 transition ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                                title="Record Audio"
                            >
                                🎤
                            </button>
                            
                            <textarea
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type a message..."
                                className="flex-1 p-2 border rounded-lg resize-none text-sm focus:ring-2 focus:ring-purple-400 outline-none max-h-24"
                                rows="1"
                                onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                            />
                            
                            <button 
                                onClick={handleSendMessage}
                                disabled={isSendingMsg || (!newMessage.trim() && !audioBlob)}
                                className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                            >
                                {isSendingMsg ? <Spinner /> : '➤'}
                            </button>
                        </div>
                        {isRecording && <p className="text-xs text-red-500 mt-1 text-center font-bold">Recording... click mic to stop.</p>}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default HomeworkRoom;